import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import Button from '../../components/ui/Button';

const HelpAndDocumentationCenter = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('auth');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedText, setCopiedText] = useState('');

  const tabs = [
    { id: 'auth', label: 'Authentifizierungsleitfaden', icon: 'Key' },
    { id: 'permissions', label: 'Rollenberechtigungen', icon: 'Shield' },
    { id: 'config', label: 'Systemkonfiguration', icon: 'Settings' },
    { id: 'production', label: 'Produktionsmodus-Leitfaden', icon: 'AlertTriangle' }
  ];

  const mockCredentials = [
    {
      role: 'Sachbearbeiter (Case Worker)',
      email: 'test.worker@ba.de',
      password: 'password123',
      description: 'Standardbenutzer für Klientenverwaltung und Fallbearbeitung',
      color: 'blue'
    },
    {
      role: 'Supervisor',
      email: 'test.supervisor@ba.de',
      password: 'supervisor456',
      description: 'Team-Überwachung, Genehmigungen und Analysen',
      color: 'purple'
    },
    {
      role: 'Administrator',
      email: 'admin.test@ba.de',
      password: 'admin789',
      description: 'Vollständige Systemverwaltung und Benutzerkontrolle',
      color: 'red'
    }
  ];

  const permissionsMatrix = [
    {
      feature: 'Klientenprofile anzeigen',
      caseWorker: 'Nur zugewiesene',
      supervisor: 'Team-weit',
      administrator: 'Alle'
    },
    {
      feature: 'Klientenprofile bearbeiten',
      caseWorker: 'Nur zugewiesene',
      supervisor: 'Team-weit',
      administrator: 'Alle'
    },
    {
      feature: 'Berichte generieren',
      caseWorker: 'Basis',
      supervisor: 'Erweitert',
      administrator: 'Alle'
    },
    {
      feature: 'Team-Übersicht',
      caseWorker: 'Nein',
      supervisor: 'Ja',
      administrator: 'Ja'
    },
    {
      feature: 'Genehmigungen erteilen',
      caseWorker: 'Nein',
      supervisor: 'Ja',
      administrator: 'Ja'
    },
    {
      feature: 'Benutzerverwaltung',
      caseWorker: 'Nein',
      supervisor: 'Nein',
      administrator: 'Ja'
    },
    {
      feature: 'Systemkonfiguration',
      caseWorker: 'Nein',
      supervisor: 'Nein',
      administrator: 'Ja'
    },
    {
      feature: 'Audit-Protokolle',
      caseWorker: 'Eigene',
      supervisor: 'Team',
      administrator: 'Alle'
    },
    {
      feature: 'Massenoperationen',
      caseWorker: 'Begrenzt',
      supervisor: 'Erweitert',
      administrator: 'Unbegrenzt'
    },
    {
      feature: 'ID-Generierung',
      caseWorker: 'Ja',
      supervisor: 'Ja',
      administrator: 'Ja'
    }
  ];

  const configSteps = [
    {
      title: 'Entwicklungsmodus (Standard)',
      icon: 'Code',
      color: 'blue',
      steps: [
        'Öffnen Sie die .env-Datei im Projektstammverzeichnis',
        'Setzen Sie: VITE_MOCK_AUTH_MODE=true',
        'Speichern Sie die Datei',
        'Starten Sie die Anwendung neu',
        'Mock-Authentifizierung ist jetzt aktiv'
      ]
    },
    {
      title: 'Produktionsmodus',
      icon: 'Rocket',
      color: 'green',
      steps: [
        'Öffnen Sie die .env-Datei',
        'Setzen Sie: VITE_MOCK_AUTH_MODE=false',
        'Stellen Sie sicher, dass VITE_SUPABASE_URL konfiguriert ist',
        'Stellen Sie sicher, dass VITE_SUPABASE_ANON_KEY konfiguriert ist',
        'Speichern Sie die Datei und starten Sie neu',
        'Supabase-Authentifizierung ist jetzt aktiv'
      ]
    }
  ];

  const productionChecklist = [
    {
      category: 'Sicherheit',
      items: [
        'SSL/TLS-Zertifikate installiert und gültig',
        'Alle API-Schlüssel in Umgebungsvariablen gespeichert',
        'Keine Test-Credentials im Code',
        'CORS-Richtlinien korrekt konfiguriert',
        'Rate-Limiting aktiviert'
      ]
    },
    {
      category: 'Datenbank',
      items: [
        'Produktionsdatenbank-Verbindung getestet',
        'Backup-Strategie implementiert',
        'RLS-Richtlinien aktiviert',
        'Indizes optimiert',
        'Migrationen erfolgreich durchgeführt'
      ]
    },
    {
      category: 'Compliance',
      items: [
        'DSGVO-Anforderungen erfüllt',
        'Audit-Protokollierung aktiviert',
        'Datenschutzerklärung aktualisiert',
        'Benutzereinwilligung implementiert',
        'Datenaufbewahrungsrichtlinien konfiguriert'
      ]
    },
    {
      category: 'Performance',
      items: [
        'Lasttest durchgeführt',
        'CDN konfiguriert',
        'Caching-Strategie implementiert',
        'Bildoptimierung aktiviert',
        'Monitoring-Tools eingerichtet'
      ]
    }
  ];

  const handleCopy = (text, label) => {
    navigator?.clipboard?.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handleSearch = (e) => {
    setSearchQuery(e?.target?.value);
  };

  const handleExport = () => {
    alert('Dokumentation wird als PDF exportiert...');
  };

  const getAccessLevelColor = (level) => {
    if (level === 'Alle' || level === 'Ja' || level === 'Unbegrenzt' || level === 'Erweitert') {
      return 'text-success bg-success/10';
    }
    if (level === 'Nein') {
      return 'text-error bg-error/10';
    }
    return 'text-warning bg-warning/10';
  };

  const currentMode = import.meta.env?.VITE_MOCK_AUTH_MODE === 'true' ? 'Entwicklung' : 'Produktion';

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="p-6 max-w-[1600px] mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-semibold text-foreground">Hilfe & Dokumentationszentrum</h1>
                <p className="text-muted-foreground mt-1">
                  Systemdokumentation, Test-Credentials und Konfigurationsanleitungen
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg border border-border">
                  <div className={`w-2 h-2 rounded-full ${
                    currentMode === 'Entwicklung' ? 'bg-warning animate-pulse' : 'bg-success'
                  }`} />
                  <span className="text-sm font-medium text-foreground">
                    Modus: {currentMode}
                  </span>
                </div>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExport}
                >
                  Dokumentation exportieren
                </Button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Hilfe-Themen durchsuchen... (z.B. 'Anmeldedaten', 'Berechtigungen', 'Produktionsmodus')"
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Environment Warning Banner */}
          {currentMode === 'Entwicklung' && (
            <div className="mb-6 bg-warning/10 border border-warning rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-warning mb-1">Entwicklungsmodus aktiv</h3>
                  <p className="text-sm text-foreground">
                    Mock-Authentifizierung ist aktiviert. Verwenden Sie die Test-Credentials unten für die Anmeldung.
                    Vor der Produktionsfreigabe muss VITE_MOCK_AUTH_MODE auf 'false' gesetzt werden.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabs Navigation */}
          <div className="bg-card rounded-lg border border-border mb-6 overflow-x-auto">
            <div className="flex">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {/* Authentication Guide Tab */}
            {activeTab === 'auth' && (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="Key" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Mock-Test-Credentials</h2>
                      <p className="text-sm text-muted-foreground">Verwenden Sie diese Anmeldedaten für Tests im Entwicklungsmodus</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {mockCredentials?.map((cred, idx) => (
                      <div key={idx} className="bg-muted/30 rounded-lg border border-border p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-8 h-8 rounded-full bg-${cred?.color}-100 dark:bg-${cred?.color}-900/30 flex items-center justify-center`}>
                            <Icon name="User" size={16} className={`text-${cred?.color}-600 dark:text-${cred?.color}-400`} />
                          </div>
                          <h3 className="font-semibold text-foreground">{cred?.role}</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">{cred?.description}</p>
                        
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium text-muted-foreground block mb-1">E-Mail</label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 px-3 py-2 bg-background rounded border border-border text-sm font-mono text-foreground">
                                {cred?.email}
                              </code>
                              <button
                                onClick={() => handleCopy(cred?.email, `email-${idx}`)}
                                className="p-2 hover:bg-muted rounded transition-colors"
                                title="Kopieren"
                              >
                                <Icon 
                                  name={copiedText === `email-${idx}` ? 'Check' : 'Copy'} 
                                  size={16} 
                                  className={copiedText === `email-${idx}` ? 'text-success' : 'text-muted-foreground'}
                                />
                              </button>
                            </div>
                          </div>
                          
                          <div>
                            <label className="text-xs font-medium text-muted-foreground block mb-1">Passwort</label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 px-3 py-2 bg-background rounded border border-border text-sm font-mono text-foreground">
                                {cred?.password}
                              </code>
                              <button
                                onClick={() => handleCopy(cred?.password, `password-${idx}`)}
                                className="p-2 hover:bg-muted rounded transition-colors"
                                title="Kopieren"
                              >
                                <Icon 
                                  name={copiedText === `password-${idx}` ? 'Check' : 'Copy'} 
                                  size={16} 
                                  className={copiedText === `password-${idx}` ? 'text-success' : 'text-muted-foreground'}
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Info" size={20} className="text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Wichtige Hinweise</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icon name="ChevronRight" size={16} className="mt-0.5 text-primary" />
                      <span>Diese Credentials funktionieren nur im Entwicklungsmodus (VITE_MOCK_AUTH_MODE=true)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="ChevronRight" size={16} className="mt-0.5 text-primary" />
                      <span>Im Produktionsmodus wird Supabase-Authentifizierung verwendet</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="ChevronRight" size={16} className="mt-0.5 text-primary" />
                      <span>Jede Rolle hat unterschiedliche Zugriffsrechte (siehe Rollenberechtigungen-Tab)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="ChevronRight" size={16} className="mt-0.5 text-primary" />
                      <span>Nach der Anmeldung werden Sie automatisch zum entsprechenden Dashboard weitergeleitet</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Keyboard" size={20} className="text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Tastaturkürzel</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded">
                      <span className="text-sm text-foreground">Schnellhilfe öffnen</span>
                      <kbd className="px-2 py-1 bg-background rounded border border-border text-xs font-mono">F1</kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded">
                      <span className="text-sm text-foreground">Suche fokussieren</span>
                      <kbd className="px-2 py-1 bg-background rounded border border-border text-xs font-mono">Ctrl+H</kbd>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Role Permissions Tab */}
            {activeTab === 'permissions' && (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="Shield" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Rollenberechtigungen-Matrix</h2>
                      <p className="text-sm text-muted-foreground">Detaillierte Zugriffsrechte für jede Benutzerrolle</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Funktion</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Sachbearbeiter</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Supervisor</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Administrator</th>
                        </tr>
                      </thead>
                      <tbody>
                        {permissionsMatrix?.map((perm, idx) => (
                          <tr key={idx} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="py-3 px-4 text-sm text-foreground font-medium">{perm?.feature}</td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getAccessLevelColor(perm?.caseWorker)}`}>
                                {perm?.caseWorker}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getAccessLevelColor(perm?.supervisor)}`}>
                                {perm?.supervisor}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getAccessLevelColor(perm?.administrator)}`}>
                                {perm?.administrator}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-card rounded-lg border border-border p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="User" size={18} className="text-blue-600" />
                      <h3 className="font-semibold text-foreground">Sachbearbeiter</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Grundlegende Zugriffsrechte für die tägliche Klientenverwaltung und Fallbearbeitung.
                    </p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Zugewiesene Klienten verwalten</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Basis-Berichte erstellen</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>IDs generieren</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-card rounded-lg border border-border p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Users" size={18} className="text-purple-600" />
                      <h3 className="font-semibold text-foreground">Supervisor</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Erweiterte Rechte für Team-Überwachung, Genehmigungen und erweiterte Analysen.
                    </p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Team-weite Klientenansicht</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Genehmigungen erteilen</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Erweiterte Berichte</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-card rounded-lg border border-border p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="ShieldCheck" size={18} className="text-red-600" />
                      <h3 className="font-semibold text-foreground">Administrator</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Vollständige Systemkontrolle, Benutzerverwaltung und Konfigurationszugriff.
                    </p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Alle Systemfunktionen</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Benutzerverwaltung</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <Icon name="Check" size={12} className="mt-0.5 text-success" />
                        <span>Systemkonfiguration</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* System Configuration Tab */}
            {activeTab === 'config' && (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="Settings" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Umgebungsmodus umschalten</h2>
                      <p className="text-sm text-muted-foreground">Schritt-für-Schritt-Anleitung zum Wechsel zwischen Entwicklungs- und Produktionsmodus</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {configSteps?.map((config, idx) => (
                      <div key={idx} className="bg-muted/30 rounded-lg border border-border p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-12 h-12 rounded-full bg-${config?.color}-100 dark:bg-${config?.color}-900/30 flex items-center justify-center`}>
                            <Icon name={config?.icon} size={24} className={`text-${config?.color}-600 dark:text-${config?.color}-400`} />
                          </div>
                          <h3 className="text-lg font-semibold text-foreground">{config?.title}</h3>
                        </div>
                        
                        <ol className="space-y-3">
                          {config?.steps?.map((step, stepIdx) => (
                            <li key={stepIdx} className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-xs font-semibold text-primary">{stepIdx + 1}</span>
                              </div>
                              <span className="text-sm text-foreground">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="FileText" size={20} className="text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Umgebungsvariablen-Referenz</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <code className="text-sm font-mono text-primary">VITE_MOCK_AUTH_MODE</code>
                          <span className="ml-2 px-2 py-0.5 bg-warning/10 text-warning text-xs rounded">Erforderlich</span>
                        </div>
                        <button
                          onClick={() => handleCopy('VITE_MOCK_AUTH_MODE=true', 'env-mock')}
                          className="p-1 hover:bg-muted rounded transition-colors"
                        >
                          <Icon 
                            name={copiedText === 'env-mock' ? 'Check' : 'Copy'} 
                            size={14} 
                            className={copiedText === 'env-mock' ? 'text-success' : 'text-muted-foreground'}
                          />
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        Steuert den Authentifizierungsmodus. Setzen Sie auf 'true' für Mock-Auth (Entwicklung) oder 'false' für Supabase (Produktion).
                      </p>
                      <div className="flex gap-2">
                        <code className="px-2 py-1 bg-background rounded text-xs font-mono">true = Entwicklung</code>
                        <code className="px-2 py-1 bg-background rounded text-xs font-mono">false = Produktion</code>
                      </div>
                    </div>

                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <code className="text-sm font-mono text-primary">VITE_SUPABASE_URL</code>
                          <span className="ml-2 px-2 py-0.5 bg-error/10 text-error text-xs rounded">Produktion</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Ihre Supabase-Projekt-URL. Erforderlich im Produktionsmodus.
                      </p>
                    </div>

                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <code className="text-sm font-mono text-primary">VITE_SUPABASE_ANON_KEY</code>
                          <span className="ml-2 px-2 py-0.5 bg-error/10 text-error text-xs rounded">Produktion</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Ihr Supabase Anonymous Key. Erforderlich im Produktionsmodus.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-warning/10 border border-warning rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-warning mb-1">Wichtiger Hinweis</h4>
                      <p className="text-sm text-foreground">
                        Nach Änderungen an der .env-Datei muss die Anwendung neu gestartet werden, damit die Änderungen wirksam werden.
                        Stellen Sie sicher, dass Sie die .env-Datei niemals in die Versionskontrolle committen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Production Mode Guide Tab */}
            {activeTab === 'production' && (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="AlertTriangle" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Produktionsfreigabe-Checkliste</h2>
                      <p className="text-sm text-muted-foreground">Überprüfen Sie alle Punkte vor der Live-Bereitstellung</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {productionChecklist?.map((category, idx) => (
                      <div key={idx} className="bg-muted/30 rounded-lg border border-border p-5">
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                          <Icon name="CheckCircle2" size={18} className="text-primary" />
                          {category?.category}
                        </h3>
                        <ul className="space-y-2">
                          {category?.items?.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start gap-2">
                              <div className="w-5 h-5 rounded border-2 border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Icon name="Check" size={12} className="text-muted-foreground" />
                              </div>
                              <span className="text-sm text-foreground">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="RotateCcw" size={20} className="text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Rollback-Verfahren</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-error/10 border border-error rounded-lg p-4">
                      <h4 className="font-semibold text-error mb-2">Bei kritischen Fehlern</h4>
                      <ol className="space-y-2 text-sm text-foreground">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold">1.</span>
                          <span>Setzen Sie VITE_MOCK_AUTH_MODE sofort zurück auf 'true'</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold">2.</span>
                          <span>Starten Sie die Anwendung neu</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold">3.</span>
                          <span>Benachrichtigen Sie das IT-Support-Team</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold">4.</span>
                          <span>Dokumentieren Sie den Fehler im Audit-Protokoll</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold">5.</span>
                          <span>Überprüfen Sie Datenbankintegrität</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Phone" size={20} className="text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Notfallkontakte</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Headphones" size={16} className="text-primary" />
                        <h4 className="font-semibold text-foreground">IT-Helpdesk</h4>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">Tel: +49 911 179-0</p>
                      <p className="text-sm text-muted-foreground">E-Mail: it-support@arbeitsagentur.de</p>
                    </div>

                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Shield" size={16} className="text-primary" />
                        <h4 className="font-semibold text-foreground">Sicherheitsteam</h4>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">Tel: +49 911 179-1234</p>
                      <p className="text-sm text-muted-foreground">E-Mail: security@arbeitsagentur.de</p>
                    </div>

                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Database" size={16} className="text-primary" />
                        <h4 className="font-semibold text-foreground">Datenbank-Admin</h4>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">Tel: +49 911 179-5678</p>
                      <p className="text-sm text-muted-foreground">E-Mail: dba@arbeitsagentur.de</p>
                    </div>
                  </div>
                </div>

                <div className="bg-success/10 border border-success rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-success mb-1">Compliance-Verifizierung</h4>
                      <p className="text-sm text-foreground">
                        Alle Produktionsfreigaben müssen vom Compliance-Team genehmigt werden.
                        Dokumentieren Sie alle Änderungen im Audit-Trail und stellen Sie sicher, dass alle DSGVO-Anforderungen erfüllt sind.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpAndDocumentationCenter;